import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { createUseStyles } from 'react-jss';
import { Switch, Select } from '@mui/material';
import { Sort, ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { useTable, useSortBy, useFilters, useExpanded, usePagination } from 'react-table';

//i18n
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableRow, MenuItem } from '@mui/material';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';

import exStyles from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js';
import styles from '../../assets/jss/material-dashboard-pro-react/components/tableStyle';
import moment from 'moment';
import 'moment/locale/is';
import TextPill from 'components/TextPill/TextPill';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Button from 'components/CustomButtons/Button.js';
import { Edit, ListAlt, ContentCopy } from '@mui/icons-material';

function DefaultColumnFilter({ column: { Header, filterValue, setFilter } }) {
    return (
        <CustomInput
            formStyle={{ marginTop: '0px', paddingTop: '0' }}
            inputProps={{
                value: filterValue || '',
                onChange: (e) => setFilter(e.target.value || undefined),
                placeholder: Header,
            }}
        />
    );
}

function SelectStatus({ column: { filterValue, setFilter, preFilteredRows, id } }) {
    return (
        <Select
            MenuProps={{ className: 'selectMenu', style: { zIndex: 35001 } }}
            classes={{ select: 'select' }}
            value={filterValue || ''}
            onChange={(e) => {
                setFilter(e.target.value || '');
            }}
            inputProps={{ name: 'status', id: 'status-select' }}
            variant="standard"
        >
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value=""
            >
                Öll
            </MenuItem>
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value="Samþykkt"
            >
                Samþykkt
            </MenuItem>
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value="Í rýni"
            >
                Í rýni
            </MenuItem>
        </Select>
    );
}

function SelectSpecial({ column: { filterValue, setFilter, preFilteredRows, id, data } }) {
    const options = React.useMemo(() => {
        const options = new Set();
        if (data && Array.isArray(data)) {
            data.forEach((row) => {
                options.add(row.name);
                // if(row.original.group_ids) {
                //   row.original.group_ids.split(',').map(x => +x).map(item => {
                //     let specFind = data.find(spec => spec.id === item)
                //     specFind && options.add(specFind.name);
                //   });
                // } else if(row.original.torgid_special_company) {
                //   return row.original.torgid_special_company.name
                // }
                // options.add(row.original.torgid_special_company ? row.original.torgid_special_company?.name : '')
            });
        }
        return [...options.values()];
    }, [id, preFilteredRows, data]);

    // Render a multi-select box
    return (
        <Select
            MenuProps={{ className: 'selectMenu', style: { zIndex: 35001 } }}
            classes={{ select: 'select' }}
            value={filterValue || ''}
            onChange={(e) => {
                setFilter(e.target.value || '');
            }}
            inputProps={{ name: 'special', id: 'special-select' }}
            variant="standard"
        >
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value=""
            >
                All
            </MenuItem>
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value="Almennir notendur"
            >
                Almennir notendur
            </MenuItem>
            {options
                .filter(function (el) {
                    return el !== '';
                })
                .map((option, i) => (
                    <MenuItem
                        key={i}
                        classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                        value={option + ' '}
                    >
                        {option}
                    </MenuItem>
                ))}
        </Select>
    );
}

function SelectCategory({ column: { filterValue, setFilter, preFilteredRows, id } }) {
    const options = React.useMemo(() => {
        const options = new Set();
        preFilteredRows.forEach((row) =>
            row.original.torgid_offer_category_nxns
                ? row.original.torgid_offer_category_nxns.map((item, i) =>
                      options.add(item.torgid_offer_category?.name + ' ')
                  )
                : ''
        );
        return [...options.values()];
    }, [id, preFilteredRows]);

    // Render a multi-select box
    return (
        <Select
            MenuProps={{ className: 'selectMenu', style: { zIndex: 35001 } }}
            classes={{ select: 'select' }}
            value={filterValue || ''}
            onChange={(e) => {
                setFilter(e.target.value || '');
            }}
            inputProps={{ name: 'category', id: 'category-select' }}
            variant="standard"
        >
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value=""
            >
                All
            </MenuItem>
            {options.sort().map((option, i) => (
                <MenuItem
                    key={i}
                    classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                    value={option}
                >
                    {option}
                </MenuItem>
            ))}
        </Select>
    );
}

function SelectActive({ column: { filterValue, setFilter, preFilteredRows, id } }) {
    return (
        <Select
            MenuProps={{ className: 'selectMenu', style: { zIndex: 35001 } }}
            classes={{ select: 'select' }}
            value={filterValue || ''}
            onChange={(e) => {
                setFilter(e.target.value || '');
            }}
            inputProps={{ name: 'active', id: 'active-select' }}
            variant="standard"
        >
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value=" "
            >
                All
            </MenuItem>
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value="Active"
            >
                Active
            </MenuItem>
            <MenuItem
                classes={{ root: 'selectMenuItem', selected: 'selectMenuItemSelected' }}
                value="Inactive"
            >
                Inactive
            </MenuItem>
        </Select>
    );
}

const style = {
    tableTitle: {
        position: 'absolute',
        top: '10px',
        color: '#09b1c5',
        fontWeight: 'bold',
        fontSize: '18px',
    },
    pagination_row: {
        margin: '10px auto',
        textAlign: 'center',
        paddingTop: '20px',
        position: 'relative',
        left: '50%',
        transform: 'translateX(-50%)',
    },
    tableBodyRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: 'var(--spara-table-row-alt, #f9f9f9)',
        },
        '&:nth-of-type(even)': {
            backgroundColor: 'var(--spara-table-row, #fff)',
        },
    },
    tableResponsive: {
        // width: "100%",
        minHeight: '0.1%',
        overflowX: 'auto',
    },
    ...exStyles,
    ...styles,
};

const useStyles = createUseStyles(style);

const OfferTable = (props) => {
    const { dataGroup, sort } = props;
    const classes = useStyles();
    const data = props.data;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cardRef = useRef();
    const [noAccessDialog, setNoAccessDialog] = useState(null); // 'edit' | 'statistics' | null
    const userStore = JSON.parse(localStorage.getItem('user') || '{}');
    const userRoles = userStore.roles || [];
    const userGroupIdsKey = (userStore.groupIds || []).join(',');
    const userGroupIds = React.useMemo(
        () =>
            userGroupIdsKey
                ? userGroupIdsKey.split(',').map(Number).filter(Boolean)
                : [],
        [userGroupIdsKey]
    );
    const hideOtherGroupsCount =
        userRoles.includes('group_admin') && !userRoles.includes('company_user');

    const prioritizeUserGroupIds = (ids) => {
        const normalized = ids.map(Number).filter(Boolean);
        return [
            ...normalized.filter((id) => userGroupIds.includes(id)),
            ...normalized.filter((id) => !userGroupIds.includes(id)),
        ];
    };

    const goToStats = (offer) => {
        const canManage = props.canManageOffer ? props.canManageOffer(offer) : true;
        if (canManage && props.reportButton) {
            navigate(`/offers/${offer.id}/statistics`);
        } else if (!canManage) {
            setNoAccessDialog('statistics');
        }
    };

    const columns = React.useMemo(
        () => [
            // {
            //   Header: '#',
            //   hideSorting: true,
            //   disableFilters: true,
            //   Cell: ({ cell }) => {
            //     return parseInt(cell.row.id)+1
            //   },
            // },
            // {
            //   Header: 'ID',
            //   accessor: 'id',
            // },
            {
                Header: t('status'),
                accessor: (values) => {
                    return values.approved ? 'Samþykkt' : 'Í rýni';
                },
                Cell: ({ cell }) => {
                    const { value, row } = cell;
                    const acceptedTextColor = '#078f5c';
                    const pendingTextColor = '#0f8cbd';

                    const textColor = value === 'Samþykkt' ? acceptedTextColor : pendingTextColor;
                    const isDisplayed = row.original.approved && row.original.is_active;
                    const isDisplayedText = isDisplayed ? 'Í birtingu' : 'Ekki í birtingu';
                    const isDisplayedColor = isDisplayed ? acceptedTextColor : pendingTextColor;

                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexGrow: 1,
                                gap: '5px',
                            }}
                        >
                            <TextPill color={textColor} text={value} />

                            <TextPill
                                style={{
                                    fontSize: '0.7rem',
                                    padding: '4px 8px',
                                }}
                                color={isDisplayedColor}
                                text={isDisplayedText}
                            />
                        </div>
                    );
                },
                Filter: SelectStatus,
                filter: 'equals',
            },
            {
                Header: t('img'),
                disableFilters: true,
                enableSorting: false,
                accessor: (values) => {
                    let img =
                        values.offer_images && values.offer_images.length > 0
                            ? values.offer_images[0].image
                            : values.image;
                    return (
                        <img
                            key={values.id + 'img'}
                            title={img}
                            src={img}
                            style={{ width: '60px', height: 'auto' }}
                        />
                    );
                },
            },
            {
                Header: t('title'),
                accessor: 'title',
            },
            ...(sort === 'activity'
                ? [
                      {
                          Header: t('opened'),
                          accessor: (values) => values.opens_count ?? 0,
                          disableFilters: true,
                          Cell: ({ cell }) => (
                              <span
                                  style={{
                                      fontWeight: 600,
                                      color: '#16B4F3',
                                      cursor: props.reportButton ? 'pointer' : 'default',
                                  }}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      goToStats(cell.row.original);
                                  }}
                              >
                                  {cell.value}
                              </span>
                          ),
                      },
                      {
                          Header: t('activated'),
                          accessor: (values) => values.activates_count ?? 0,
                          disableFilters: true,
                          Cell: ({ cell }) => (
                              <span
                                  style={{
                                      fontWeight: 600,
                                      color: '#008B6B',
                                      cursor: props.reportButton ? 'pointer' : 'default',
                                  }}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      goToStats(cell.row.original);
                                  }}
                              >
                                  {cell.value}
                              </span>
                          ),
                      },
                      {
                          Header: t('favourited'),
                          accessor: (values) => values.favourites_count ?? 0,
                          disableFilters: true,
                          Cell: ({ cell }) => (
                              <span
                                  style={{
                                      fontWeight: 600,
                                      color: '#DE2C48',
                                      cursor: props.reportButton ? 'pointer' : 'default',
                                  }}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      goToStats(cell.row.original);
                                  }}
                              >
                                  {cell.value}
                              </span>
                          ),
                      },
                  ]
                : []),
            {
                Header: t('company'),
                accessor: (values) => {
                    return values.torgid_company.name;
                },
            },
            {
                Header: t('special'),
                accessor: (values) => {
                    if (values.group_ids) {
                        return values.group_ids
                            .split(',')
                            .map((x) => +x)
                            .map((item) => dataGroup?.find((spec) => spec.id === item)?.name + ' ');
                    } else if (values.torgid_special_company) {
                        return values.torgid_special_company.name + ' ';
                    } else {
                        return 'Almennir notendur ';
                    }
                },
                Cell: ({ cell }) => {
                    const { value, row } = cell;
                    if (row.original.group_ids) {
                        const ids = prioritizeUserGroupIds(
                            row.original.group_ids.split(',')
                        );
                        const known = ids.filter((item) =>
                            dataGroup?.find((spec) => Number(spec.id) === item)
                        );
                        const unknownCount = ids.length - known.length;

                        return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px' }}>
                                {known.map((item, i) => {
                                    const specItem = dataGroup.find(
                                        (spec) => Number(spec.id) === item
                                    );
                                    return (
                                        <img
                                            key={item + '_' + i + '_specImg'}
                                            src={specItem.logo}
                                            title={specItem.name}
                                            style={{ width: '40px', height: 'auto', padding: '4px' }}
                                        />
                                    );
                                })}
                                {unknownCount > 0 && !hideOtherGroupsCount && (
                                    <span
                                        key="unknown_count"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            background: '#e0e0e0',
                                            color: '#888',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        +{unknownCount} {t('other_groups')}
                                    </span>
                                )}
                                {ids.length === 0 && 'Almennir notendur'}
                            </span>
                        );
                    } else if (row.original.torgid_special_company) {
                        return (
                            <img
                                key={value.id + 'spec'}
                                src={row.original.torgid_special_company.logo}
                                style={{ width: '40px', height: 'auto', padding: '4px' }}
                            />
                        );
                    } else {
                        return 'Almennir notendur';
                    }
                },
                Filter: SelectSpecial,
                data: dataGroup,
                filter: 'includes',
                minWidth: 100,
            },
            {
                Header: t('categories'),
                accessor: (values) => {
                    return values.torgid_offer_category_nxns
                        ? values.torgid_offer_category_nxns.map((item, i) => {
                              return item.torgid_offer_category?.name + ' ';
                          })
                        : '';
                },
                Cell: ({ cell }) => {
                    const { value, row } = cell;
                    return (
                        //return parent categories icons
                        <div>
                            {row.original.torgid_offer_category_nxns &&
                                row.original.torgid_offer_category_nxns.map((item, i) => {
                                    if (!item.torgid_offer_category) return null;
                                    return item.torgid_offer_category.icon &&
                                        item.torgid_offer_category.name ? (
                                        <img
                                            key={i}
                                            src={item.torgid_offer_category.icon}
                                            title={item.torgid_offer_category.name}
                                            style={{
                                                width: '25px',
                                                height: '25px',
                                                padding: '4px',
                                            }}
                                        />
                                    ) : null;
                                })}
                        </div>
                    );
                },
                Filter: SelectCategory,
                filter: 'includes',
                minWidth: 120,
            },
            {
                Header: t('offer.start_date'),
                // Format start date with moment
                accessor: (values) => {
                    if (!moment(values.start_date).isValid()) {
                        return '';
                    }
                    return moment(values.start_date).format('DD.MM.YYYY');
                },

                minWidth: 110,
            },
            {
                Header: t('expires_on'),
                accessor: (values) => {
                    if (!moment(values.end_date).isValid()) {
                        return '';
                    }
                    return moment(values.end_date).format('DD.MM.YYYY');
                },
                minWidth: 120,
            },
            {
                Header: t('is_active'),
                accessor: (values) => {
                    return values.is_active ? 'Active ' : 'Inactive ';
                },
                Cell: ({ cell }) => {
                    const { value } = cell;
                    const canManage = props.canManageOffer
                        ? props.canManageOffer(cell.row.original)
                        : true;
                    if (!canManage) {
                        return (
                            <div
                                onClick={() => setNoAccessDialog('edit')}
                                style={{ display: 'inline-flex', cursor: 'pointer' }}
                            >
                                <Switch checked={value === 'Active '} disabled />
                            </div>
                        );
                    }
                    return (
                        <Switch
                            checked={value === 'Active '}
                            onChange={() => props.changeOfferActive(cell.row.original)}
                        />
                    );
                },
                Filter: SelectActive,
                filter: 'includes',
            },
            {
                Header: t('actions'),
                hideSorting: true,
                disableFilters: true,
                Cell: ({ cell }) => {
                    const canManage = props.canManageOffer
                        ? props.canManageOffer(cell.row.original)
                        : true;
                    return (
                        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: 4 }}>
                            {props.updateButton && canManage && props.updateButton(cell.row.original)}
                            {props.updateButton && !canManage && (
                                <Button
                                    color="success"
                                    className={classes.actionButton}
                                    style={{ margin: 0, opacity: 0.4 }}
                                    title={t('edit')}
                                    onClick={() => setNoAccessDialog('edit')}
                                >
                                    <Edit className={classes.icon} />
                                </Button>
                            )}
                            {props.onDuplicate && canManage && (
                                <Button
                                    color="info"
                                    className={classes.actionButton}
                                    style={{ margin: 0 }}
                                    title={t('offer.duplicate')}
                                    onClick={() => props.onDuplicate(cell.row.original)}
                                >
                                    <ContentCopy className={classes.icon} />
                                </Button>
                            )}
                            {props.onDuplicate && !canManage && (
                                <Button
                                    color="info"
                                    className={classes.actionButton}
                                    style={{ margin: 0, opacity: 0.4 }}
                                    title={t('offer.duplicate')}
                                    onClick={() => setNoAccessDialog('edit')}
                                >
                                    <ContentCopy className={classes.icon} />
                                </Button>
                            )}
                            {props.deleteButton &&
                                canManage &&
                                props.deleteButton(cell.row.original)}
                            {props.notificationButton &&
                                props.notificationButton(cell.row.original)}
                            {props.reportButton &&
                                canManage &&
                                props.reportButton(cell.row.original)}
                            {props.reportButton && !canManage && (
                                <Button
                                    color="warning"
                                    className={classes.actionButton}
                                    style={{ margin: 0, opacity: 0.4 }}
                                    title={t('statistic')}
                                    onClick={() => setNoAccessDialog('statistics')}
                                >
                                    <ListAlt className={classes.icon} />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ],
        [dataGroup, sort, hideOtherGroupsCount, userGroupIds, t]
    );

    // Use the state and functions returned from useTable to build your UI
    const { getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
        {
            columns,
            data,
            defaultColumn: { Filter: DefaultColumnFilter },
            autoResetExpanded: false,
            autoResetFilters: false,
            autoResetSortBy: false,
        },
        useFilters,
        useSortBy,
        useExpanded
    );

    const generateSortingIndicator = (column) => {
        return column.isSorted ? (
            column.isSortedDesc ? (
                <ArrowDropDown />
            ) : (
                <ArrowDropUp />
            )
        ) : (
            <Sort style={{ fontSize: '14px' }} />
        );
    };

    // Render the UI for your table
    return (
        <React.Fragment>
            <ConfirmDialog
                open={Boolean(noAccessDialog)}
                type="info"
                title={t(
                    noAccessDialog === 'statistics'
                        ? 'offer.no_access_stats_title'
                        : 'offer.no_access_title'
                )}
                message={t(
                    noAccessDialog === 'statistics'
                        ? 'offer.no_access_stats_message'
                        : 'offer.no_access_message'
                )}
                onConfirm={() => setNoAccessDialog(null)}
                confirmText={t('close')}
            />
            <div className={classes.tableResponsive}>
                <div ref={cardRef} />
                <Table className={classes.table}>
                    <TableHead>
                        {headerGroups.map((headerGroup) => (
                            <TableRow
                                className={classes.tableRow + ' ' + classes.tableRowHead}
                                {...headerGroup.getHeaderGroupProps()}
                            >
                                {headerGroup.headers.map((column) => (
                                    <TableCell
                                        variant="head"
                                        className={classes.tableHeadCell}
                                        {...column.getHeaderProps({
                                            style: { minWidth: column.minWidth },
                                        })}
                                    >
                                        {column.render('Header')}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {rows.map((row, key) => {
                            prepareRow(row);
                            return (
                                <React.Fragment key={row.getRowProps().key}>
                                    <TableRow
                                        id={row?.original?.id}
                                        key={key}
                                        hover={true}
                                        className={classes.tableBodyRow}
                                    >
                                        {row.cells.map((cell) => (
                                            <TableCell
                                                className={classes.tableCell}
                                                key={key}
                                                {...cell.getCellProps()}
                                            >
                                                {cell.render('Cell')}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </React.Fragment>
    );
};

export default OfferTable;
